import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Product.css';
import axiosInstance from '../../../Config/axiosInstance';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Select from 'react-select';
import { Snackbar, Typography, Alert } from '@mui/material';

const EditProduct = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [filteredSubcategories, setFilteredSubcategories] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [dimensions, setDimensions] = useState([]);
    const [isTitleManuallyEdited, setIsManuallyEdited] = useState(false);
    const [autoGeneratedTitle, setAutoGeneratedTitle] = useState("");

    //definirea optiunilor pentru status
    const statusOptions = [
        {value: "ACTIVE", label: 'Active'},
        {value: "INACTIVE", label: 'Inactive'}
    ];
    
    //state-uri pentru validare si notificari
    const [titleError, setTitleError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error"
    });
    const [originalTitle, setOriginalTitle] = useState('');

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        category: "",
        subcategory: "",
        material: "",
        dimenssions: "",
        color: "",
        status: "",
    });

    //daca utilizatorul sters complet campul title, revenim la modul automat
    const handleTitleBlur = (e) => {
        if(e.target.value === "" && isTitleManuallyEdited) {
            setIsManuallyEdited(false)
        }
    }

    //functie pentru generarea titlului automat
    const generateAutoTitle = useCallback(async () => {
        //verifica daca avem cel putin un camp completat
        const hasData = formData.category || formData.subcategory || formData.material || formData.dimenssions || formData.color || formData.description;
        if(!hasData) {
            setAutoGeneratedTitle("");
            return;
        }

        try {
            const response = await axiosInstance.post("/product/generate-title-preview/", {
                category: formData.category ? parseInt(formData.category) : null,
                subcategory: formData.subcategory ? parseInt(formData.subcategory) : null,
                material: formData.material ? parseInt(formData.material) : null,
                dimenssions: formData.dimenssions ? parseInt(formData.dimenssions) : null,
                color: formData.color || null,
                description: formData.description || null
            });
            setAutoGeneratedTitle(response.data.title);
        } catch (error) {
            console.error("Error generating auto title: ", error);
            setAutoGeneratedTitle("Error generating title");
        }
    }, [formData.category, formData.subcategory, formData.material, formData.dimenssions, formData.color, formData.description]);

    //functie pentru verificare existentei titlului 
    const checkProductTitleExists = async(title) => {
        if(!title.trim()) {
            return false;
        }

        try {
            const response = await axiosInstance.get("/product/check-name/", {
                params: {name: title.trim(), exclude_id: id}
            });
            return response.data.exists;
        } catch (error) {
            console.error("Error checking product title: ", error);
            return false;
        }
    }

    //reseteaza eroarea atunci cand titlul se schimba
    useEffect(() => {
        if(titleError) {
            setTitleError("");
        }
    }, [formData.title, titleError]);

    useEffect(() => {
        //nu genera titlul automat daca utilizatorul a editat manual
        if(isTitleManuallyEdited) {
            return;
        }
        const timer = setTimeout(() => {
            generateAutoTitle();
        }, 500);

        return () => clearTimeout(timer);
    }, [
        formData.category,
        formData.subcategory,
        formData.material,
        formData.dimenssions,
        formData.color,
        formData.description, 
        isTitleManuallyEdited,
        generateAutoTitle
    ]);

    //actualizeaza campul title doar daca nu a fost editat manual
    useEffect(() => {
        if(!isTitleManuallyEdited && autoGeneratedTitle && autoGeneratedTitle !== formData.title) {
            setFormData(prev => ({
                ...prev, 
                title: autoGeneratedTitle
            }));
        }
    }, [autoGeneratedTitle, isTitleManuallyEdited, formData.title]);

    const handleCloseSnackbar = () => {
        setSnackbar({...snackbar, open: false});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if(name === "title") {
            setIsManuallyEdited(true);
            setFormData(prev => ({
                ...prev,
                title: value,
            }));
        } else if (name === "category") {
            setFormData(prevState => ({
                ...prevState,
                category: value,
                subcategory: "",
            }));

            const filtered = subcategories.filter(subcat => 
                subcat.category === parseInt(value)
            );
            setFilteredSubcategories(filtered);
        } else {
            setFormData(prevState => ({
                ...prevState,
                [name]: value,
            }));
        }

        //resetare eroare la schimbarea campurilor care afecteaza titlul
        if(name === 'material' || name === 'dimenssions' || name === 'color' || name === 'category' || name === 'subcategory') {
            if(titleError) {
                setTitleError("");
            }
        }
    };

    //încarcarea datelor într-un singur Promise.all pentru a evita problemele de sincronizare
    useEffect(() => {
        const fetchData = async () => {
            try {
                //încarcă toate datele în paralel
                const [catRes, subcatRes, matRes, dimRes, productRes] = await Promise.all([
                    axiosInstance.get('/category/'),
                    axiosInstance.get('/subcategory/'),
                    axiosInstance.get('/type-materials/'),
                    axiosInstance.get('/dimensions/'),
                    axiosInstance.get(`/product/${id}/`)
                ]);

                const productData = productRes.data;
                const categoriesData = catRes.data;
                const subcategoriesData = subcatRes.data;
                const materialsData = matRes.data;
                const dimensionsData = dimRes.data;

                //setează toate listele de opțiuni
                setCategories(categoriesData);
                setSubcategories(subcategoriesData);
                setMaterials(materialsData);
                setDimensions(dimensionsData);

                //filtrează subcategoriile pentru categoria produsului
                const filtered = subcategoriesData.filter(subcat => 
                    subcat.category === parseInt(productData.category)
                );
                setFilteredSubcategories(filtered);

                //setează formData după ce avem toate datele
                setFormData({
                    title: productData.title || "",
                    description: productData.description || "", 
                    category: productData.category || "", 
                    subcategory: productData.subcategory || "",
                    material: productData.material || "",
                    dimenssions: productData.dimenssions || "",
                    color: productData.color || "",
                    status: productData.status || "",
                });

                //salvează titlul original pentru comparație
                setOriginalTitle(productData.title || "");

                //generează titlul automat pentru a verifica dacă a fost editat manual
                try {
                    const response = await axiosInstance.post("/product/generate-title-preview/", {
                        category: productData.category ? parseInt(productData.category) : null,
                        subcategory: productData.subcategory ? parseInt(productData.subcategory) : null,
                        material: productData.material ? parseInt(productData.material) : null,
                        dimenssions: productData.dimenssions ? parseInt(productData.dimenssions) : null,
                        color: productData.color || null,
                        description: productData.description || null
                    });
                    
                    //dacă titlul este diferit de cel generat automat, înseamnă că a fost setat manual
                    if (response.data.title !== productData.title) {
                        setIsManuallyEdited(true);
                    } else {
                        setIsManuallyEdited(false);
                    }
                } catch (error) {
                    //in caz de eroare, presupunem că e manual
                    setIsManuallyEdited(true);
                }

            } catch (error) {
                console.error("Error loading product data:", error);
                setSnackbar({
                    open: true,
                    message: "Error loading product data. Please try again.",
                    severity: "error"
                });
            }
        };
        fetchData();
    }, [id]);

    //useEffect pentru a actualiza subcategoriile când se schimbă categoria în timpul editării
    useEffect(() => {
        if (formData.category && subcategories.length > 0) {
            const filtered = subcategories.filter(
                subcat => subcat.category === parseInt(formData.category)
            );
            setFilteredSubcategories(filtered);

            //verifică dacă subcategoria curentă aparține noii categorii
            if (formData.subcategory && !filtered.some(s => s.id === parseInt(formData.subcategory))) {
                setFormData(prev => ({ ...prev, subcategory: "" }));
            }
        } else if (!formData.category) {
            setFilteredSubcategories([]);
            if (formData.subcategory) {
                setFormData(prev => ({ ...prev, subcategory: "" }));
            }
        }
    }, [formData.category, subcategories, formData.subcategory]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        //previne submitul multiplu
        if(isSubmitting) return;
        setIsSubmitting(true);
        
        //verifica daca titlul este gol
        if(!formData.title.trim()) {
            setTitleError("Title is required.");
            setSnackbar({
                open: true,
                message: "Title is required.",
                severity: "error"
            });
            setIsSubmitting(false);
            return;
        }

        //verifica daca titlul s a schimbat si este existent in sistem
        if (formData.title.trim() !== originalTitle) {
            try {
                const exists = await checkProductTitleExists(formData.title);
                if(exists) {
                    setTitleError("A product with this name already exists. Please choose a different title.");
                    setSnackbar({
                        open: true,
                        message: "A product with this name already exists. Please choose a different title.",
                        severity: "error"
                    });
                    setIsSubmitting(false);
                    return;
                }
            } catch (error) {
                setSnackbar({
                    open: true, 
                    message: "Error checking product existence. Please try again.",
                    severity: "error"
                });
                setIsSubmitting(false);
                return;
            }
        }

        //actualizeaza produsul
        try {
            const response = await axiosInstance.put(`/product/${id}/`, formData);
            console.log("Updated successfully:", response.data);
            
            setSnackbar({
                open: true,
                message: "Product updated successfully!",
                severity: "success"
            });

            setTimeout(() => {
                navigate(-1);
            }, 1500);
        } catch (error) {
            console.error("Error updating product:", error.response?.data || error.message);
            
            //verifica daca eroarea este de la titlu duplicat
            if(error.response?.status === 400) {
                const errorData = error.response.data;
                
                if (typeof errorData === 'object') {
                    if (errorData.title && Array.isArray(errorData.title) && 
                        errorData.title[0]?.includes("already exists")) {
                        setTitleError("The name of this product already exists in the system.");
                        setSnackbar({
                            open: true,
                            message: "A product with this name already exists. Please choose different title.",
                            severity: "error"
                        });
                    } 
                    else if (errorData.non_field_errors && 
                             errorData.non_field_errors[0]?.includes("already exists")) {
                        setTitleError("The name of this product already exists in the system.");
                        setSnackbar({
                            open: true,
                            message: "A product with this name already exists. Please choose different title.",
                            severity: "error"
                        });
                    }
                    else if (errorData.detail?.includes("already exists")) {
                        setTitleError("The name of this product already exists in the system.");
                        setSnackbar({
                            open: true,
                            message: "A product with this name already exists. Please choose different title.",
                            severity: "error"
                        });
                    } else {
                        setSnackbar({
                            open: true,
                            message: "An error occurred. Please try again.",
                            severity: "error"
                        });
                    }
                } else {
                    setSnackbar({
                        open: true,
                        message: "An error occurred. Please try again.",
                        severity: "error"
                    });
                }
            } else {
                setSnackbar({
                    open: true,
                    message: "An error occurred. Please try again.",
                    severity: "error"
                });
            }
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    //reseteaza isSubmitting cand titlul este modificat pentru a putea retrimite datele pentru cazul titlu unic
    useEffect(() => {
        if (isSubmitting && titleError) {
            setIsSubmitting(false);
        }
    }, [formData.title, titleError, isSubmitting]);

    return (
        <div className='product-page'>
        <div className='add-product-container'>
            <div className='product-header'>
                <ArrowBackIcon className='back-arrow' onClick={() => navigate(-1)} />
                <h2 className='add-product-title'>Edit Product</h2>
            </div> 
        </div>

        <hr className='title-separator'/>
            
        <div className='add-product-container-form'>
            <form onSubmit={handleSubmit} className='add-material-form'>
                    <div className='form-group'>
                        <label htmlFor='title' className='label'>
                            Title<span className='required-star'>*</span>
                            {formData.title && (
                                <span className='auto-badge'>
                                    {isTitleManuallyEdited ? 'set manually' : 'auto-generated'}
                                </span>
                            )}
                        </label>
                        <input 
                            id="title"
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            onBlur={handleTitleBlur}
                            className={`input ${isTitleManuallyEdited ? 'manually-edited' : 'auto-generated'}`}
                            required
                        />
                        <div className='title-info'>                     
                            <Typography variant='caption' color='primary'>
                                The title is generated automatically as you select the fields below, or you can add a custom title.
                            </Typography>
                        </div>
                        {titleError && (
                            <Typography
                                variant='caption'
                                color='error'
                                sx={{mt: 1, display: "block"}}
                            >
                                {titleError}
                            </Typography>
                        )}
                    </div>

                    <div className='form-group'>
                        <label htmlFor='description' className='label'>Description</label>
                        <textarea 
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            className='textarea'
                        />
                    </div>

                    <div className='form-group'>
                        <label htmlFor='category' className='label'>Category</label>
                        <Select 
                            classNamePrefix="react-select"
                            className="react-select"
                            options={categories.map(cat => ({
                                value: cat.id,
                                label: cat.title,
                            }))}
                            value={
                                categories
                                    .map(cat => ({ value: cat.id, label: cat.title }))
                                    .find(c => c.value === parseInt(formData.category)) || null
                            }
                            onChange={(selectedOption) => 
                                setFormData(prev => ({
                                    ...prev,
                                    category: selectedOption ? selectedOption.value : "",
                                }))
                            }
                            placeholder="-- Select Category --"
                            isClearable
                        />
                    </div>

                    <div className='form-group'>
                        <label htmlFor='subcategory' className='label'>Subcategory</label>
                        <Select 
                            classNamePrefix="react-select"
                            className="react-select"
                            options={filteredSubcategories.map(subcat => ({
                                value: subcat.id,
                                label: subcat.title,
                            }))}
                            value={
                                filteredSubcategories
                                    .map(subcat => ({ value: subcat.id, label: subcat.title }))
                                    .find(opt => opt.value === parseInt(formData.subcategory)) || null
                            }
                            onChange={(selectedOption) => 
                                setFormData(prev => ({
                                    ...prev,
                                    subcategory: selectedOption ? selectedOption.value : "",
                                }))
                            }
                            placeholder="-- Select Subcategory --"
                            isClearable
                        />
                    </div>

                    <div className='form-group-row'>
                        <div className='form-group'>
                            <label htmlFor='dimenssions' className='label'>Dimension</label>
                            <Select 
                                classNamePrefix="react-select"
                                className="react-select"
                                options={dimensions.map(dim => ({
                                    value: dim.id,
                                    label: dim.dimenssions,
                                }))}
                                value={
                                    dimensions
                                        .map(dim => ({value: dim.id, label: dim.dimenssions}))
                                        .find(opt => opt.value === parseInt(formData.dimenssions)) || null
                                }
                                onChange={(selectedOption) => 
                                    setFormData(prev => ({
                                        ...prev,
                                        dimenssions: selectedOption ? selectedOption.value : "",
                                    }))
                                }
                                placeholder="-- Select Dimension --"
                                isClearable
                            />
                        </div>

                        <div className='form-group'>
                            <label htmlFor='material' className='label'>Type of Materials</label>
                            <Select 
                                classNamePrefix="react-select"
                                className='react-select'
                                options={materials.map(mat => ({
                                    value: mat.id,
                                    label: mat.name,
                                }))}
                                value={
                                    materials
                                        .map(mat => ({value: mat.id, label: mat.name}))
                                        .find(opt => opt.value === parseInt(formData.material)) || null
                                }
                                onChange={(selectedOption) => 
                                    setFormData(prev => ({
                                        ...prev,
                                        material: selectedOption ? selectedOption.value : "",
                                    }))
                                }
                                placeholder="-- Select Material --"
                                isClearable
                            />
                        </div>
                    </div>

                    <div className='form-group'>
                        <label htmlFor="color" className='label'>Color</label>
                        <input 
                            id="color"
                            type="text"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            className='input'
                        />
                    </div>

                    <div className='form-group'>
                        <label htmlFor='status' className='label'>Status</label>
                        <Select
                            classNamePrefix="react-select"
                            className='react-select'
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === formData.status) || statusOptions[0]}
                            onChange={(selectedOption) => 
                                setFormData(prev => ({
                                    ...prev, 
                                    status: selectedOption ? selectedOption.value: 'ACTIVE',
                                }))
                            }
                            placeholder = '--Select Status--'
                        />
                    </div>

                    <button 
                        type="submit" 
                        className='button-save'
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Updating..." : "Update"}
                    </button>
                </form>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{vertical: "top", horizontal: "center"}}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
};

export default EditProduct;